import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PoolDetail } from '../PoolDetail';
import type {
  NoCardEntryRegisterDialogConfiQuery,
  NoCardEntryRegisterDialogConfiQueryVariables,
} from './__generated__/index.graphql';

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  padding: var(--triple-unit);
  margin: auto;
  @media ${laptopAndAbove} {
    width: 480px;
  }
`;
const EmailContent = styled(BodyM)`
  text-align: left;
  border-radius: var(--unit);
  padding: var(--double-unit);
  background: var(--c-black);
  color: var(--c-nd-600);
`;
const Block = styled.div`
  gap: var(--triple-unit);
`;
const Subtitle = styled(BodyM)`
  color: var(--c-nd-600);
`;
const LoaderWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  min-height: 200px;
`;

const messages = defineMessages({
  title: {
    id: 'NoCardEntryRegisterDialog.title',
    defaultMessage: 'No Card Entry',
  },
});

interface Props {
  sport: Sport;
  emailAddress: string | undefined;
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  eventType: So5FixtureEvent;
}

const NO_CARD_ENTRY_REGISTER_DIALOG_QUERY = gql`
  query NoCardEntryRegisterDialogConfiQuery(
    $sport: Sport!
    $eventType: So5FixtureEvent!
  ) {
    config {
      id
      so5 {
        id
        noCardRoute(sport: $sport, eventType: $eventType) {
          id
          maxLineupsPerUser
          limitedMaxLineupsPerUser: maxLineupsPerUser(rarity: limited)
          rareMaxLineupsPerUser: maxLineupsPerUser(rarity: rare)
          superRareMaxLineupsPerUser: maxLineupsPerUser(rarity: super_rare)
          uniqueMaxLineupsPerUser: maxLineupsPerUser(rarity: unique)
          ...PoolDetail_noCardRoute
        }
      }
    }
  }
  ${PoolDetail.fragments.noCardRoute}
` as TypedDocumentNode<
  NoCardEntryRegisterDialogConfiQuery,
  NoCardEntryRegisterDialogConfiQueryVariables
>;

export const So5NoCardEntryRegisterDialog = (props: Props) => {
  const { sport, eventType, open, emailAddress } = props;
  const [loadConfig, { data, loading }] = useLazyQuery(
    NO_CARD_ENTRY_REGISTER_DIALOG_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: { sport, eventType },
    }
  );

  const {
    maxLineupsPerUser,
    limitedMaxLineupsPerUser,
    rareMaxLineupsPerUser,
    superRareMaxLineupsPerUser,
    uniqueMaxLineupsPerUser,
  } = data?.config?.so5?.noCardRoute || {};

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open, loadConfig]);

  return (
    <Dialog
      maxWidth={false}
      title={
        <HeadlineM className="text-center" as="h1">
          <FormattedMessage {...messages.title} />
        </HeadlineM>
      }
      {...props}
    >
      <DialogContainer>
        {loading ? (
          <LoaderWrapper>
            <LoadingIndicator />
          </LoaderWrapper>
        ) : (
          <>
            <Block>
              <Subtitle>
                <FormattedMessage
                  id="NoCardEntryRegisterDialog.subtitle1"
                  defaultMessage="You can play in this Game Week without owning NFT cards by selecting a team of players among those Sorare will give you access to."
                />
              </Subtitle>
            </Block>
            <Block>
              <Subtitle>
                <FormattedMessage
                  id="NoCardEntryRegisterDialog.subtitle2"
                  defaultMessage="For this Game Week, you will be entitled to select until {maxLineupsPerUser} teams of players
                                  ({limitedMaxLineupsPerUser} Limited, {rareMaxLineupsPerUser} Rare, {superRareMaxLineupsPerUser} Super Rare, {uniqueMaxLineupsPerUser} Unique)
                                  by following the process described below."
                  values={{
                    maxLineupsPerUser,
                    limitedMaxLineupsPerUser,
                    rareMaxLineupsPerUser,
                    superRareMaxLineupsPerUser,
                    uniqueMaxLineupsPerUser,
                  }}
                />
              </Subtitle>
            </Block>
            <Block>
              <Subtitle>
                <PoolDetail noCardRoute={data?.config?.so5?.noCardRoute} />
              </Subtitle>
            </Block>
            <Block>
              <BodyM>
                <FormattedMessage
                  id="NoCardEntryRegisterDialog.subtitle3"
                  defaultMessage={`To participate:{br}
                             1. Send an email to {email}{br}
                             2. Use the following template:`}
                  values={{
                    br: <br />,
                    email: (
                      <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
                    ),
                  }}
                />
              </BodyM>
            </Block>
            <Block>
              <EmailContent>
                <FormattedMessage
                  id="NoCardEntryRegisterDialog.emailContent"
                  defaultMessage="I, surname last name, registered on Sorare with the email address XXXX and the username XXX request to get the link to participate without cards in the Game Week XXX."
                />
              </EmailContent>
            </Block>
            <Block>
              <BodyM>
                <FormattedMessage
                  id="NoCardEntryRegisterDialog.subtitle4"
                  defaultMessage="3. Check your inbox (and the spams folder)"
                />
              </BodyM>
            </Block>
          </>
        )}
      </DialogContainer>
    </Dialog>
  );
};
